import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { Scale, Loader2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertBanner } from '@/components/ui/alert-banner';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

interface UnbookedProduct { id: string; name: string; sku: string | null; qty: number; cost: number; value: number }
interface StockLedgerCheck {
  stockValue: number;
  account1200: number;
  difference: number;
  unbookedProducts: UnbookedProduct[];
  unbookedTotal: number;
  unexplained: number;
}
interface PostResult { dryRun: boolean; posted: number; total: number; products: UnbookedProduct[] }

/**
 * POS-I246: "Stock vs ledger". Read-only check of the stock valuation against Inventory account 1200 and
 * the products that hold stock with no stock-in movement. The one write is the guarded
 * "Post opening balances" action: permission-gated, previewed first (dry run), the owner types the
 * Rs total to confirm, idempotent on the server. It never runs by itself.
 */
export function StockLedgerCheckCard() {
  const { t } = useTranslation('finance');
  const qc = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canPost =
    (hasPermission('inventory:update') || hasPermission('inventory:*')) &&
    (hasPermission('accounting:create') || hasPermission('accounting:*'));
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const { data, isLoading, isError } = useQuery<StockLedgerCheck>({
    queryKey: ['stock-ledger-check'],
    queryFn: () => api.get('/reports/stock-ledger-check').then((r) => r.data),
    retry: false,
  });

  const preview = useMutation<PostResult>({
    mutationFn: () => api.post('/reports/stock-ledger-check/post-opening-balances', { dryRun: true }).then((r) => r.data),
    onSuccess: () => { setTyped(''); setOpen(true); },
    onError: (err) => notify.error(err, t('slcPreviewFailed')),
  });

  const post = useMutation<PostResult, Error, number>({
    mutationFn: (confirmTotal) =>
      api.post('/reports/stock-ledger-check/post-opening-balances', { dryRun: false, confirmTotal }).then((r) => r.data),
    onSuccess: (res) => {
      toast.success(t('slcPosted', { count: res.posted }));
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['stock-ledger-check'] });
      void qc.invalidateQueries({ queryKey: ['report-inventory-valuation'] });
    },
    onError: (err) => notify.error(err, t('slcPostFailed')),
  });

  if (isError) return null;
  const plan = preview.data;
  const confirmed = plan ? Math.abs(Number(typed.replace(/,/g, '')) - plan.total) < 0.005 && typed.trim() !== '' : false;
  const inSync = data ? Math.abs(data.difference) < 0.01 : false;

  return (
    <Card className="animate-fade-in-up" data-testid="stock-ledger-check">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Scale className="h-5 w-5 text-info-text" /> {t('slcTitle')}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">{t('slcSubtitle')}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{t('slcLoading')}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border p-4 text-center">
                <p className="text-[11px] text-muted-foreground font-medium">{t('slcStockValue')}</p>
                <p className="mt-1 text-xl font-bold font-mono"><bdi dir="ltr">{formatCurrency(data.stockValue)}</bdi></p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <p className="text-[11px] text-muted-foreground font-medium">{t('slcAccount1200')}</p>
                <p className="mt-1 text-xl font-bold font-mono"><bdi dir="ltr">{formatCurrency(data.account1200)}</bdi></p>
              </div>
              <div className={`rounded-lg border p-4 text-center ${inSync ? 'bg-success/10' : 'bg-warning/10'}`}>
                <p className="text-[11px] text-muted-foreground font-medium">{t('slcDifference')}</p>
                <p className={`mt-1 text-xl font-bold font-mono ${inSync ? 'text-success-text' : 'text-warning-text'}`}><bdi dir="ltr">{formatCurrency(data.difference)}</bdi></p>
              </div>
            </div>

            {inSync ? (
              <p className="flex items-center gap-2 text-sm text-success-text"><CheckCircle2 className="h-4 w-4" />{t('slcInSync')}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('slcExplain')}</p>
            )}

            {data.unbookedProducts.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t('slcUnbookedTitle', { count: data.unbookedProducts.length, total: formatCurrency(data.unbookedTotal) })}</p>
                <div className="overflow-auto max-h-64 rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b text-muted-foreground">
                        <th className="px-3 py-2 text-start font-medium">{t('product')}</th>
                        <th className="px-3 py-2 text-start font-medium">{t('sku')}</th>
                        <th className="px-3 py-2 text-end font-medium">{t('stockQty')}</th>
                        <th className="px-3 py-2 text-end font-medium">{t('costPrice')}</th>
                        <th className="px-3 py-2 text-end font-medium">{t('slcValue')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.unbookedProducts.map((p) => (
                        <tr key={p.id}>
                          <td className="px-3 py-2 font-medium break-words">{p.name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{p.sku || '—'}</td>
                          <td className="px-3 py-2 text-end">{p.qty}</td>
                          <td className="px-3 py-2 text-end font-mono"><bdi dir="ltr">{formatCurrency(p.cost)}</bdi></td>
                          <td className="px-3 py-2 text-end font-mono font-semibold"><bdi dir="ltr">{formatCurrency(p.value)}</bdi></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {Math.abs(data.unexplained) >= 0.01 && (
                  <p className="text-xs text-muted-foreground">{t('slcUnexplained', { amount: formatCurrency(data.unexplained) })}</p>
                )}
                {canPost ? (
                  <Button size="sm" onClick={() => preview.mutate()} disabled={preview.isPending}>
                    {preview.isPending && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
                    {t('slcPostButton')}
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('slcNoPermission')}</p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog
        visible={open}
        onHide={() => setOpen(false)}
        header={t('slcDialogTitle')}
        style={{ width: '36rem', maxWidth: '95vw' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
            <Button
              onClick={() => plan && post.mutate(plan.total)}
              disabled={!plan || plan.products.length === 0 || !confirmed || post.isPending}
            >
              {post.isPending && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('slcConfirmPost')}
            </Button>
          </div>
        }
      >
        {plan && (
          <div className="space-y-3 text-sm">
            {plan.products.length === 0 ? (
              <p>{t('slcNothingToPost')}</p>
            ) : (
              <>
                <AlertBanner tone="warning" className="mb-0">{t('slcWarning', { count: plan.products.length, total: formatCurrency(plan.total) })}</AlertBanner>
                <p className="text-muted-foreground">{t('slcJournalNote')}</p>
                <div className="max-h-48 overflow-auto rounded-lg border">
                  <table className="w-full">
                    <tbody className="divide-y">
                      {plan.products.map((p) => (
                        <tr key={p.id}>
                          <td className="px-3 py-1.5 break-words">{p.name}</td>
                          <td className="px-3 py-1.5 text-end font-mono"><bdi dir="ltr">{p.qty} × {formatCurrency(p.cost)}</bdi></td>
                          <td className="px-3 py-1.5 text-end font-mono font-semibold"><bdi dir="ltr">{formatCurrency(p.value)}</bdi></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <Label className="mb-1.5 block">{t('slcTypeTotal', { total: plan.total.toFixed(2) })}</Label>
                  <Input
                    inputMode="decimal"
                    dir="ltr"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder={plan.total.toFixed(2)}
                    className="h-9"
                    autoFocus
                    data-testid="slc-confirm-total"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Dialog>
    </Card>
  );
}
